import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();

  const handleMonthlySubscription = () => {
    // Store selected plan before redirecting to Stripe
    localStorage.setItem('selectedPlan', 'monthly');
    window.location.href = 'https://buy.stripe.com/28E9ATdvAbgU3wY3RIbII00';
  };

  const handleYearlySubscription = () => {
    // Store selected plan before redirecting to Stripe
    localStorage.setItem('selectedPlan', 'yearly');
    window.location.href = 'https://buy.stripe.com/14A9ATcrw4Sw8RidsibII01';
  };

  const handleSkip = () => {
    // Complete registration without payment
    const token = localStorage.getItem('pendingToken');
    const user = localStorage.getItem('pendingUser');
    
    if (token && user) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', user);
      localStorage.removeItem('pendingToken');
      localStorage.removeItem('pendingUser');
      navigate('/dashboard');
      window.location.reload(); // Reload to update auth state
    } else {
      navigate('/');
    }
  };

  const features = [
    'Organization Directory Access',
    'Direct Messaging with File Attachments',
    'Post Unlimited Opportunities',
    'Create & Manage Events',
    'Social Media Sharing Tools',
    'Mobile-Optimized Experience',
    'Profile Customization',
    'Network with Nonprofits & Service Organizations',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex justify-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_bc318d90-16f9-4f9c-83f4-4cb66d5436f1/artifacts/7ff6752k_CEC%20Connect_GraphicLogo-black.png" 
            alt="CEC Connect" 
            className="h-16 w-auto"
          />
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Connect with nonprofits and service organizations. Start making an impact today!
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
            {/* Monthly Plan */}
            <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-green-400" data-testid="monthly-plan-card">
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl mb-2">Monthly Plan</CardTitle>
                <CardDescription>Perfect for getting started</CardDescription>
                <div className="mt-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-gray-900">$4.44</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Billed monthly</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleMonthlySubscription}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
                  data-testid="monthly-subscribe-button"
                >
                  Subscribe Monthly
                </Button>
                <div className="space-y-3 pt-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className="hover:shadow-2xl transition-all duration-300 border-2 border-green-500 relative" data-testid="yearly-plan-card">
              {/* Best Value Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-green-600 text-white px-4 py-1 flex items-center gap-1">
                  <Sparkles className="h-4 w-4" />
                  BEST VALUE - Save 17%
                </Badge>
              </div>

              <CardHeader className="text-center pb-4 pt-8">
                <CardTitle className="text-2xl mb-2">Yearly Plan</CardTitle>
                <CardDescription>Save with annual billing</CardDescription>
                <div className="mt-4">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-green-600">$44.44</span>
                    <span className="text-gray-600 ml-2">/year</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    That's only <span className="font-semibold text-green-600">$3.70/month</span>
                  </p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleYearlySubscription}
                  className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6"
                  data-testid="yearly-subscribe-button"
                >
                  Subscribe Yearly
                </Button>
                <div className="space-y-3 pt-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Skip Option */}
          <div className="text-center">
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
              data-testid="skip-pricing-button"
            >
              Skip for now, continue to dashboard
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">Trusted by nonprofits and service organizations</p>
            <div className="flex justify-center items-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-sm">Cancel Anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-sm">No Hidden Fees</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
